import React, { useMemo } from 'react';
import { Box, useTheme } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
	NoRowsErrorOverlay,
	NoRowsOverlay,
} from '@/components/table/DataGridComponents/NoRowsOverlay';
import DataGridColumnMenu from '@/components/table/DataGridComponents/DataGridColumnMenu';
import { useDataGridColumnProps } from '@/context/columnControlContext';
import { PAGE_SIZE_OPTIONS } from '../useAdminListParams';
import { AdminGridToolbar } from './AdminGridToolbar';
import { AdminGridFooter } from './AdminGridFooter';

/**
 * The grid inside AdminDataGrid (which provides the saved column settings it reads); see
 * AdminDataGrid for its props.
 */
export const AdminDataGridContent = ({
	columns,
	rows,
	isLoading,
	isError,
	objectType,
	toolbarActions,
	showToolbar = true,
	showColumnsButton = true,
	footerContent,
	isRowClickable = () => true,
	minHeight = '12em',
	maxHeight,
	height,
	...gridProps
}) => {
	const theme = useTheme();
	// The column menu only offers "Hide column", so columns that can't be hidden have no menu,
	// and nor does a table without the toolbar's Columns button, the only way to show a
	// hidden column again. display 'flex' centres each cell's content vertically, as rows size
	// to their content.
	const hasColumnsButton = showToolbar && showColumnsButton;
	const columnsWithMenus = useMemo(
		() =>
			columns.map((column) => ({
				display: 'flex',
				...column,
				disableColumnMenu: !hasColumnsButton || column.hideable === false,
			})),
		[columns, hasColumnsButton],
	);
	const columnProps = useDataGridColumnProps(columnsWithMenus);

	const Overlay = isError
		? () => <NoRowsErrorOverlay objectType={objectType} />
		: () => <NoRowsOverlay objectType={objectType} />;

	return (
		<Box
			sx={{
				border: `1px solid ${theme.palette.divider}`,
				borderRadius: '8px',
				backgroundColor: theme.palette.background.paper,
				display: 'flex',
				flexDirection: 'column',
				minHeight,
				maxHeight,
				...(height && { height }),
			}}
		>
			<DataGrid
				{...columnProps}
				rows={rows}
				loading={isLoading}
				pageSizeOptions={PAGE_SIZE_OPTIONS}
				disableColumnFilter
				// The toolbar's Columns button shows and hides columns
				disableColumnSelector
				disableRowSelectionOnClick
				// Rows fit their content, e.g. a chips cell that expands
				getRowHeight={() => 'auto'}
				sortingOrder={['desc', 'asc']}
				getRowClassName={({ row }) =>
					gridProps.onRowClick && isRowClickable(row) ? 'admin-clickable-row' : ''
				}
				slots={{
					...(showToolbar && { toolbar: AdminGridToolbar }),
					...(footerContent && { footer: AdminGridFooter }),
					columnMenu: DataGridColumnMenu,
					noRowsOverlay: Overlay,
					noResultsOverlay: Overlay,
				}}
				slotProps={{
					toolbar: { actions: toolbarActions, showColumnsButton },
					footer: { content: footerContent },
					loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' },
				}}
				sx={{
					border: 0,
					borderRadius: '8px',
					// Cell and header styles as in the CRM tables
					'& .MuiDataGrid-cell': { outline: 'none', padding: 1, minHeight: '42px' },
					'& .MuiDataGrid-columnHeader': { borderRight: 'none !important' },
					'--DataGrid-overlayHeight': '3em',
					'& .admin-clickable-row': { cursor: 'pointer' },
				}}
				{...gridProps}
			/>
		</Box>
	);
};

AdminDataGridContent.displayName = 'AdminDataGridContent';
